import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Shield, Wifi } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface EmployeeReportData {
  id: string;
  firstName: string;
  lastName: string;
  workLocation: string;
  totalRemoteDays: number;
  currentStartDate: string | null;
  currentEndDate: string | null;
  insuranceActive: boolean;
  vpnActive: boolean;
}

interface EmployeeReportingTableProps {
  company: Company | null;
}

const EmployeeReportingTable = ({ company }: EmployeeReportingTableProps) => {
  const [employees, setEmployees] = useState<EmployeeReportData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company) {
      fetchEmployeeData();
    }
  }, [company]);

  const fetchEmployeeData = async () => {
    if (!company) return;

    try {
      // Fetch employees with their remote work requests
      const { data: employeesData } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          current_country,
          residence_country,
          position
        `)
        .eq('company_id', company.id);

      if (!employeesData) return;

      // For each employee, calculate their remote work data
      const employeeReports = await Promise.all(
        employeesData.map(async (emp) => {
          // Get all approved remote work requests for this employee
          // Use the secure RPC function
          const { data: allRequests } = await supabase
            .rpc('get_company_remote_work_requests');

          const requests = (allRequests || [])
            .filter(req => req.employee_id === emp.id && req.status === 'approved')
            .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());

          // Calculate total remote days
          let totalDays = 0;
          let currentStartDate = null;
          let currentEndDate = null;
          let hasInsurance = false;
          let hasVpn = false;

          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];

          if (requests) {
            requests.forEach((req) => {
              const startDate = new Date(req.start_date);
              const endDate = new Date(req.end_date);
              
              // Calculate days for completed or ongoing requests
              const effectiveEndDate = endDate > today ? today : endDate;
              if (effectiveEndDate >= startDate) {
                const timeDiff = effectiveEndDate.getTime() - startDate.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
                totalDays += daysDiff;
              }

              // Check if currently remote
              if (req.start_date <= todayStr && req.end_date >= todayStr) {
                currentStartDate = req.start_date;
                currentEndDate = req.end_date;
                hasInsurance = req.insurance_active || false;
                hasVpn = req.vpn_active || false;
              }
            });
          }

          return {
            id: emp.id,
            firstName: emp.first_name || 'N/A',
            lastName: emp.last_name || 'N/A',
            workLocation: emp.current_country || emp.residence_country || 'Non renseigné',
            totalRemoteDays: totalDays,
            currentStartDate,
            currentEndDate,
            insuranceActive: hasInsurance,
            vpnActive: hasVpn
          };
        })
      );

      setEmployees(employeeReports);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Suivi détaillé des employés
        </CardTitle>
        <CardDescription>
          Informations complètes sur l'activité de télétravail de chaque employé
        </CardDescription>
      </CardHeader>
      <CardContent>
        {employees.length === 0 ? (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucun employé trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead>Lieu de travail</TableHead>
                  <TableHead className="text-center">Jours télétravail</TableHead>
                  <TableHead>Période actuelle</TableHead>
                  <TableHead className="text-center">Assurance</TableHead>
                  <TableHead className="text-center">VPN</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <div className="font-medium">
                        {employee.firstName} {employee.lastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {employee.workLocation}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-mono">
                        {employee.totalRemoteDays} jours
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {employee.currentStartDate && employee.currentEndDate ? (
                        <div className="text-sm">
                          <div>Du {new Date(employee.currentStartDate).toLocaleDateString('fr-FR')}</div>
                          <div>Au {new Date(employee.currentEndDate).toLocaleDateString('fr-FR')}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Pas en télétravail</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Shield className={`h-4 w-4 ${employee.insuranceActive ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className={`text-sm ${employee.insuranceActive ? 'text-green-600' : 'text-gray-400'}`}>
                          {employee.insuranceActive ? 'Assurance active' : 'Assurance inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Wifi className={`h-4 w-4 ${employee.vpnActive ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className={`text-sm ${employee.vpnActive ? 'text-green-600' : 'text-gray-400'}`}>
                          {employee.vpnActive ? 'VPN Actif' : 'VPN Inactif'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeReportingTable;